import { Timestamp } from 'firebase/firestore';

/**
 * SickLeaveModel - Represents a single sick leave day for a staff member
 * Each sick leave day is stored as a separate document in Firestore
 *
 * Collection Path: companies/{companyId}/sickLeave/{sickLeaveId}
 *
 * IMPORTANT:
 * - Dates are stored as strings in "YYYY-MM-DD" format to avoid timezone issues
 * - Duration equals the scheduled shift minutes for that day
 * - A sick day is only recorded when the staff member has a scheduled shift on that day
 * - No entitlement system — record-only, year-scoped running total, no carry-over
 */
export class SickLeaveModel {
  constructor({
    id = null,
    staffId,
    staffName = '',       // Denormalized, not stored in Firestore
    departmentId,
    departmentName = '',  // Denormalized, not stored in Firestore
    date,
    durationMinutes = 0,
    createdBy,
    createdAt = new Date()
  }) {
    this.id = id;
    this.staffId = staffId;
    this.staffName = staffName;
    this.departmentId = departmentId;
    this.departmentName = departmentName;

    // Store date as string in YYYY-MM-DD format
    this.date = this._toDateString(date);

    this.durationMinutes = durationMinutes;
    this.createdBy = createdBy;
    this.createdAt = createdAt instanceof Date ? createdAt : createdAt?.toDate();
  }

  /**
   * Convert date to YYYY-MM-DD string format
   * @private
   */
  _toDateString(date) {
    if (!date) return null;

    // Already a string in correct format
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }

    // Convert Date object to string
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    // Firestore Timestamp
    if (date.toDate && typeof date.toDate === 'function') {
      const d = date.toDate();
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    // Try parsing as date string
    try {
      const d = new Date(date);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      console.error('Invalid date format:', date);
    }

    return null;
  }

  /**
   * Convert date string to Date object for display
   * Parses YYYY-MM-DD as local date to avoid timezone shift
   * @private
   */
  _toDateObject() {
    if (!this.date) return null;
    const [year, month, day] = this.date.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  /**
   * Convert sick leave model to Firestore document format
   */
  toFirestore() {
    return {
      staffId: this.staffId,
      departmentId: this.departmentId,
      date: this.date,
      durationMinutes: this.durationMinutes,
      createdBy: this.createdBy,
      createdAt: Timestamp.fromDate(this.createdAt)
    };
  }

  /**
   * Create sick leave model from Firestore document
   */
  static fromFirestore(doc) {
    if (!doc.exists()) return null;

    const data = doc.data();

    return new SickLeaveModel({
      id: doc.id,
      staffId: data.staffId,
      departmentId: data.departmentId,
      date: data.date,
      durationMinutes: data.durationMinutes || 0,
      createdBy: data.createdBy,
      createdAt: data.createdAt?.toDate()
    });
  }

  /**
   * Get formatted date string (DD.MM.YYYY)
   */
  getFormattedDate() {
    const dateObj = this._toDateObject();
    if (!dateObj) return '';

    return dateObj.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Get date string in YYYY-MM-DD format (for comparisons)
   */
  getDateString() {
    return this.date;
  }

  /**
   * Get formatted duration string (e.g., "8h", "7h 45min")
   */
  getFormattedDuration() {
    return SickLeaveHelpers.formatDurationFromMinutes(this.durationMinutes);
  }

  /**
   * Get duration in hours
   */
  getDurationInHours() {
    return this.durationMinutes / 60;
  }

  /**
   * Check if sick leave day has no scheduled shift
   */
  hasNoShift() {
    return this.durationMinutes === 0;
  }

  /**
   * Get display text for duration
   */
  getDurationDisplayText() {
    if (this.hasNoShift()) {
      return '0 Stunden (Kein Dienst geplant)';
    }
    return `${this.getFormattedDuration()} (${this.durationMinutes} Minuten)`;
  }

  /**
   * Get short display text for duration (just the time)
   */
  getDurationShortText() {
    if (this.hasNoShift()) {
      return '0h';
    }
    return this.getFormattedDuration();
  }

  /**
   * Update duration from shift minutes
   * Called by cascade sync helpers in SickLeaveService when shifts are edited
   */
  updateFromShift(shiftDurationMinutes) {
    this.durationMinutes = shiftDurationMinutes || 0;
    this.lastRecalculatedAt = new Date();
  }

  /**
   * Reset duration to 0 (when shift is deleted)
   * Called by cascade sync helpers in SickLeaveService
   */
  resetDuration() {
    this.durationMinutes = 0;
  }
}

/**
 * SickLeaveDateRange - Helper class for working with date ranges
 * Useful if a future UI allows selecting a date range to mark multiple sick days at once
 */
export class SickLeaveDateRange {
  constructor(startDate, endDate) {
    this.startDate = this._parseDate(startDate);
    this.endDate = this._parseDate(endDate);
  }

  /**
   * Parse date to Date object
   * @private
   */
  _parseDate(date) {
    if (date instanceof Date) {
      return date;
    }

    // Firestore Timestamp
    if (date?.toDate && typeof date.toDate === 'function') {
      return date.toDate();
    }

    // String format
    if (typeof date === 'string') {
      const [year, month, day] = date.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    return new Date(date);
  }

  /**
   * Get array of dates between start and end (inclusive)
   */
  getDates() {
    const dates = [];
    const currentDate = new Date(this.startDate);

    while (currentDate <= this.endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  }

  /**
   * Get total number of days
   */
  getTotalDays() {
    return this.getDates().length;
  }

  /**
   * Validate date range
   */
  isValid() {
    return this.startDate <= this.endDate;
  }

  /**
   * Check if range is too long (warning threshold)
   */
  isTooLong(maxDays = 10) {
    return this.getTotalDays() > maxDays;
  }

  /**
   * Check if date is too far in future (warning threshold)
   */
  isFarFuture(maxYears = 2) {
    const futureLimit = new Date();
    futureLimit.setFullYear(futureLimit.getFullYear() + maxYears);
    return this.endDate > futureLimit;
  }
}

/**
 * SickLeaveSummary - Summary statistics for sick leave
 * Year-scoped running total — no entitlement, no carry-over fields
 */
export class SickLeaveSummary {
  constructor() {
    this.totalDays = 0;
    this.totalMinutes = 0;
    this.byStaff = new Map(); // staffId -> { staffId, name, days, minutes }
  }

  /**
   * Add sick leave entry to summary
   */
  addSickLeave(sickLeave, staffName) {
    this.totalDays++;
    this.totalMinutes += sickLeave.durationMinutes;

    if (!this.byStaff.has(sickLeave.staffId)) {
      this.byStaff.set(sickLeave.staffId, {
        staffId: sickLeave.staffId,
        name: staffName,
        days: 0,
        minutes: 0
      });
    }

    const staffStats = this.byStaff.get(sickLeave.staffId);
    staffStats.days++;
    staffStats.minutes += sickLeave.durationMinutes;
  }

  /**
   * Get top N staff members by sick leave days
   */
  getTopStaff(limit = 3) {
    return Array.from(this.byStaff.values())
      .sort((a, b) => b.days - a.days)
      .slice(0, limit);
  }

  /**
   * Get formatted total duration
   */
  getFormattedTotalDuration() {
    return SickLeaveHelpers.formatDurationFromMinutes(this.totalMinutes);
  }

  /**
   * Get total duration in hours
   */
  getTotalHours() {
    return this.totalMinutes / 60;
  }
}

/**
 * Validation functions for sick leave data
 */
export const SickLeaveValidation = {
  /**
   * Validate sick leave data before save
   */
  validate(sickLeaveData) {
    const errors = [];

    if (!sickLeaveData.staffId) {
      errors.push('Mitarbeiter ist erforderlich');
    }

    if (!sickLeaveData.departmentId) {
      errors.push('Abteilung ist erforderlich');
    }

    if (!sickLeaveData.date) {
      errors.push('Datum ist erforderlich');
    }

    if (sickLeaveData.durationMinutes < 0) {
      errors.push('Dauer kann nicht negativ sein');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

/**
 * Helper functions for sick leave operations
 */
export const SickLeaveHelpers = {
  /**
   * Format duration from minutes to human-readable format
   * Examples:
   * - 0 min   -> "0h"
   * - 60 min  -> "1h"
   * - 90 min  -> "1h 30min"
   * - 465 min -> "7h 45min"
   * - 480 min -> "8h"
   */
  formatDurationFromMinutes(minutes) {
    if (!minutes || minutes === 0) {
      return '0h';
    }

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (mins === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${mins}min`;
  },

  /**
   * Convert minutes to hours (decimal)
   */
  minutesToHours(minutes) {
    return minutes / 60;
  },

  /**
   * Convert hours to minutes
   */
  hoursToMinutes(hours) {
    return Math.round(hours * 60);
  },

  /**
   * Generate sick leave document ID
   * Format: {staffId}_{YYYY-MM-DD}_{departmentId}
   * Deterministic ID prevents duplicates without a separate existence query
   */
  generateSickLeaveId(staffId, date, departmentId) {
    let dateStr;

    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      dateStr = date;
    } else if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    } else if (date?.toDate && typeof date.toDate === 'function') {
      const d = date.toDate();
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    } else {
      dateStr = date;
    }

    return `${staffId}_${dateStr}_${departmentId}`;
  },

  /**
   * Parse sick leave document ID back to components
   */
  parseSickLeaveId(sickLeaveId) {
    const parts = sickLeaveId.split('_');
    return {
      staffId: parts[0],
      dateStr: parts[1],
      departmentId: parts[2]
    };
  },

  /**
   * Format date for display
   * Accepts string "YYYY-MM-DD" or Date object
   */
  formatDate(date) {
    let dateObj;

    if (typeof date === 'string') {
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day);
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (date?.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else {
      return '';
    }

    return dateObj.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  },

  /**
   * Format date with day name
   * Accepts string "YYYY-MM-DD" or Date object
   */
  formatDateWithDay(date) {
    let dateObj;

    if (typeof date === 'string') {
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day);
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (date?.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else {
      return '';
    }

    return dateObj.toLocaleDateString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  },

  /**
   * Get week number for a date
   */
  getWeekNumber(date) {
    let dateObj;

    if (typeof date === 'string') {
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      dateObj = new Date(date);
    }

    const firstDayOfYear = new Date(dateObj.getFullYear(), 0, 1);
    const pastDaysOfYear = (dateObj - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  },

  /**
   * Check if two dates are the same day
   * Accepts string "YYYY-MM-DD" or Date objects
   */
  isSameDay(date1, date2) {
    const str1 = typeof date1 === 'string' ? date1 :
      date1 instanceof Date ?
        `${date1.getFullYear()}-${String(date1.getMonth() + 1).padStart(2, '0')}-${String(date1.getDate()).padStart(2, '0')}` :
        '';

    const str2 = typeof date2 === 'string' ? date2 :
      date2 instanceof Date ?
        `${date2.getFullYear()}-${String(date2.getMonth() + 1).padStart(2, '0')}-${String(date2.getDate()).padStart(2, '0')}` :
        '';

    return str1 === str2;
  }
};

export default SickLeaveModel;